// src/components/CMS/Preview/PreviewPanel.jsx

import { useState, useEffect, useRef } from 'react';
import {
  Monitor,
  Tablet,
  Smartphone,
  X,
  Maximize2,
  Minimize2,
  RefreshCw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import './PreviewPanel.css';

const DEVICES = {
  desktop: {
    name: 'Desktop',
    icon: Monitor,
    width: 1920,
    height: 1080,
    scale: 0.5,
  },
  tablet: {
    name: 'Tablet',
    icon: Tablet,
    width: 768,
    height: 1024,
    scale: 0.65,
  },
  mobile: {
    name: 'Mobile',
    icon: Smartphone,
    width: 375,
    height: 812,
    scale: 0.8,
  },
};

/**
 * PreviewPanel - Responsive preview for CMS content.
 *
 * @param {Object} props
 * @param {Object} props.content - TipTap JSON content
 * @param {string} props.title - Content title
 * @param {string} props.excerpt - Content excerpt
 * @param {Object} props.featuredImage - Featured image object
 * @param {boolean} props.isOpen - Whether the panel is open
 * @param {Function} props.onClose - Close handler
 */
const PreviewPanel = ({
  content,
  title,
  excerpt,
  featuredImage,
  isOpen,
  onClose,
}) => {
  const [activeDevice, setActiveDevice] = useState('desktop');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const iframeRef = useRef(null);

  const device = DEVICES[activeDevice];

  // Generate HTML from TipTap content
  const generatePreviewHTML = () => {
    const contentHTML = content ? tiptapToHTML(content) : '<p>No content yet...</p>';

    return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title || 'Preview'}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #1f2937;
      padding: 40px;
      max-width: 800px;
      margin: 0 auto;
    }

    .preview-header {
      margin-bottom: 32px;
    }

    .preview-title {
      font-size: 2.5rem;
      font-weight: 700;
      line-height: 1.2;
      margin-bottom: 16px;
    }

    .preview-excerpt {
      font-size: 1.25rem;
      color: #6b7280;
      line-height: 1.6;
    }

    .preview-featured-image {
      width: 100%;
      height: auto;
      border-radius: 12px;
      margin-bottom: 32px;
    }

    .preview-content {
      font-size: 1.125rem;
    }

    .preview-content h1 {
      font-size: 2rem;
      margin: 1.5em 0 0.5em;
    }

    .preview-content h2 {
      font-size: 1.5rem;
      margin: 1.5em 0 0.5em;
    }

    .preview-content h3 {
      font-size: 1.25rem;
      margin: 1.25em 0 0.5em;
    }

    .preview-content p {
      margin: 1em 0;
    }

    .preview-content ul,
    .preview-content ol {
      margin: 1em 0;
      padding-left: 1.5em;
    }

    .preview-content li {
      margin: 0.5em 0;
    }

    .preview-content blockquote {
      border-left: 4px solid #3b82f6;
      padding-left: 1em;
      margin: 1em 0;
      font-style: italic;
      color: #4b5563;
    }

    .preview-content img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
    }

    .preview-content code {
      background: #f3f4f6;
      padding: 0.2em 0.4em;
      border-radius: 4px;
      font-size: 0.9em;
    }

    .preview-content pre {
      background: #1f2937;
      color: #f3f4f6;
      padding: 1em;
      border-radius: 8px;
      overflow-x: auto;
      margin: 1em 0;
    }

    .preview-content pre code {
      background: none;
      padding: 0;
    }

    .preview-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 1em 0;
    }

    .preview-content th,
    .preview-content td {
      border: 1px solid #e5e7eb;
      padding: 0.75em;
      text-align: left;
    }

    .preview-content th {
      background: #f9fafb;
      font-weight: 600;
    }

    .preview-content hr {
      border: none;
      border-top: 1px solid #e5e7eb;
      margin: 2em 0;
    }

    .preview-content a {
      color: #3b82f6;
      text-decoration: underline;
    }

    /* Custom Blocks */
    .faq-block {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      margin: 1em 0;
    }

    .faq-item {
      border-bottom: 1px solid #e5e7eb;
    }

    .faq-item:last-child {
      border-bottom: none;
    }

    .faq-question {
      padding: 1em;
      font-weight: 600;
      background: #f9fafb;
      cursor: pointer;
    }

    .faq-answer {
      padding: 1em;
    }

    .alert-block {
      padding: 1em;
      border-radius: 8px;
      margin: 1em 0;
    }

    .alert-info {
      background: #dbeafe;
      color: #1e40af;
    }

    .alert-warning {
      background: #fef3c7;
      color: #92400e;
    }

    .alert-error {
      background: #fee2e2;
      color: #991b1b;
    }

    .alert-success {
      background: #d1fae5;
      color: #065f46;
    }

    .cta-block {
      background: #3b82f6;
      color: white;
      padding: 2em;
      border-radius: 12px;
      text-align: center;
      margin: 2em 0;
    }

    .cta-block h3 {
      margin-bottom: 0.5em;
    }

    .cta-block button {
      background: white;
      color: #3b82f6;
      border: none;
      padding: 0.75em 1.5em;
      border-radius: 6px;
      font-weight: 600;
      margin-top: 1em;
      cursor: pointer;
    }

    @media (max-width: 768px) {
      body {
        padding: 24px;
      }

      .preview-title {
        font-size: 1.75rem;
      }

      .preview-excerpt {
        font-size: 1rem;
      }

      .preview-content {
        font-size: 1rem;
      }
    }

    @media (max-width: 375px) {
      body {
        padding: 16px;
      }

      .preview-title {
        font-size: 1.5rem;
      }
    }
  </style>
</head>
<body>
  <article>
    <header class="preview-header">
      <h1 class="preview-title">${title || 'Untitled'}</h1>
      ${excerpt ? `<p class="preview-excerpt">${excerpt}</p>` : ''}
    </header>

    ${featuredImage ? `<img class="preview-featured-image" src="${featuredImage.url}" alt="${featuredImage.alt_text || title}" />` : ''}

    <div class="preview-content">
      ${contentHTML}
    </div>
  </article>
</body>
</html>
    `;
  };

  // Convert TipTap JSON to HTML
  const tiptapToHTML = (doc) => {
    if (!doc || !doc.content) return '';

    return doc.content.map(node => renderNode(node)).join('');
  };

  const renderNode = (node) => {
    if (!node) return '';

    switch (node.type) {
      case 'paragraph':
        return `<p>${renderContent(node.content)}</p>`;

      case 'heading':
        const level = node.attrs?.level || 2;
        return `<h${level}>${renderContent(node.content)}</h${level}>`;

      case 'bulletList':
        return `<ul>${node.content?.map(item => renderNode(item)).join('') || ''}</ul>`;

      case 'orderedList':
        return `<ol>${node.content?.map(item => renderNode(item)).join('') || ''}</ol>`;

      case 'listItem':
        return `<li>${node.content?.map(child => renderNode(child)).join('') || ''}</li>`;

      case 'blockquote':
        return `<blockquote>${node.content?.map(child => renderNode(child)).join('') || ''}</blockquote>`;

      case 'codeBlock':
        const language = node.attrs?.language || '';
        return `<pre><code class="language-${language}">${renderContent(node.content)}</code></pre>`;

      case 'image':
        return `<img src="${node.attrs?.src}" alt="${node.attrs?.alt || ''}" />`;

      case 'horizontalRule':
        return '<hr />';

      case 'table':
        return `<table>${node.content?.map(row => renderNode(row)).join('') || ''}</table>`;

      case 'tableRow':
        return `<tr>${node.content?.map(cell => renderNode(cell)).join('') || ''}</tr>`;

      case 'tableHeader':
        return `<th>${renderContent(node.content)}</th>`;

      case 'tableCell':
        return `<td>${renderContent(node.content)}</td>`;

      case 'youtube':
        return `<iframe width="560" height="315" src="https://www.youtube.com/embed/${node.attrs?.videoId}" frameborder="0" allowfullscreen></iframe>`;

      // Custom blocks
      case 'faq':
        return `<div class="faq-block">${node.content?.map(item =>
          `<div class="faq-item">
            <div class="faq-question">${item.attrs?.question || ''}</div>
            <div class="faq-answer">${item.attrs?.answer || ''}</div>
          </div>`
        ).join('') || ''}</div>`;

      case 'alert':
        const alertType = node.attrs?.type || 'info';
        return `<div class="alert-block alert-${alertType}">${renderContent(node.content)}</div>`;

      case 'cta':
        return `<div class="cta-block">
          <h3>${node.attrs?.title || 'Call to Action'}</h3>
          <p>${node.attrs?.description || ''}</p>
          <button>${node.attrs?.buttonText || 'Learn More'}</button>
        </div>`;

      default:
        if (node.content) {
          return node.content.map(child => renderNode(child)).join('');
        }
        return '';
    }
  };

  const renderContent = (content) => {
    if (!content) return '';

    return content.map(node => {
      if (node.type === 'text') {
        let text = node.text || '';

        // Apply marks
        if (node.marks) {
          node.marks.forEach(mark => {
            switch (mark.type) {
              case 'bold':
                text = `<strong>${text}</strong>`;
                break;
              case 'italic':
                text = `<em>${text}</em>`;
                break;
              case 'underline':
                text = `<u>${text}</u>`;
                break;
              case 'strike':
                text = `<s>${text}</s>`;
                break;
              case 'code':
                text = `<code>${text}</code>`;
                break;
              case 'link':
                text = `<a href="${mark.attrs?.href || '#'}">${text}</a>`;
                break;
              case 'highlight':
                text = `<mark>${text}</mark>`;
                break;
            }
          });
        }

        return text;
      }

      return renderNode(node);
    }).join('');
  };

  // Update iframe content
  useEffect(() => {
    if (isOpen && iframeRef.current) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(generatePreviewHTML());
      doc.close();
    }
  }, [isOpen, content, title, excerpt, featuredImage, activeDevice]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 150));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };

  const handleRefresh = () => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(generatePreviewHTML());
      doc.close();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`preview-panel ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* Header */}
      <div className="preview-header">
        <div className="preview-devices">
          {Object.entries(DEVICES).map(([key, dev]) => (
            <button
              key={key}
              className={`device-button ${activeDevice === key ? 'active' : ''}`}
              onClick={() => setActiveDevice(key)}
              title={dev.name}
            >
              <dev.icon size={18} />
              <span>{dev.name}</span>
            </button>
          ))}
        </div>

        <div className="preview-info">
          <span>{device.width} x {device.height}</span>
        </div>

        <div className="preview-controls">
          <button
            className="control-button"
            onClick={handleZoomOut}
            title="Zoom Out"
          >
            <ZoomOut size={16} />
          </button>
          <span className="zoom-level">{zoom}%</span>
          <button
            className="control-button"
            onClick={handleZoomIn}
            title="Zoom In"
          >
            <ZoomIn size={16} />
          </button>
          <button
            className="control-button"
            onClick={handleRefresh}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
          <button
            className="control-button"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button
            className="control-button close"
            onClick={onClose}
            title="Close Preview"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Preview Container */}
      <div className="preview-container">
        <div
          className="preview-frame"
          style={{
            width: device.width,
            height: device.height,
            transform: `scale(${(zoom / 100) * device.scale})`,
          }}
        >
          <iframe
            ref={iframeRef}
            title="Content Preview"
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
};

export default PreviewPanel;
